import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  CreditCard as CardIcon,
  QrCode as QrIcon,
  Payment as PaymentIcon,
  Inventory as InventoryIcon,
  Tune as TuneIcon,
  MoreHoriz as OtherIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  SwapHoriz as ExchangeIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { roundMoney } from '../../../../utils/money';
import { isCourtesyCatalogLine } from '../../../../utils/sales/line-pricing.utils';
import { formatPriceAdjustmentDisplay } from '../../../../utils/formatters/money';
import { useOtherPaymentMethods } from '../../../../hooks/useOtherPaymentMethods';
import { sectionPhaseSx } from '../../shared/salesStep2Phase.styles';
import { PaymentMethod } from '../../../../api/models/PaymentMethod';
import {
  DiscountConfig,
  MarkupConfig,
  SalesOrder,
  ExchangeFormData,
  ExchangeSummary,
  SelectedProduct,
} from '../../../../types/sales.types';
import { PAYMENT_METHODS } from '../../../../utils/constants/paymentMethods';
import { FlagSwitch } from '../../../../components/ui';
import SalesStepHint from '../../shared/SalesStepHint/SalesStepHint';
import LinePricingToolbar from '../../shared/LinePricingToolbar/LinePricingToolbar';
import SalesLinePricingCard from '../../shared/SalesLinePricingCard/SalesLinePricingCard';
import ProductImagePreview, { ProductImageData } from '../../shared/ProductImagePreview';
import {
  PaymentToggleBox,
  PaymentMethodButton,
  PaymentMethodIconBox,
  ProductCardBox,
  ValidationList,
  ModalCancelButton,
  SubStepGhostContinueButton,
} from '../../SalesStep2/SalesStep2.styles';
import { ExchangeStep2FlowPhase, useExchangeStep2Flow } from './ExchangeStep2Payment.hooks';
import ExchangePaymentAmountDialog from './ExchangePaymentAmountDialog';
import { getExchangeCreditViolationMessage } from './exchangePricing.utils';
import {
  ExchangeStep2MainContainer as MainContainer,
  ExchangeStep2LeftColumn as LeftColumn,
  ExchangeStep2RightColumn as RightColumn,
} from './ExchangeStep2Payment.styles';

const PAYMENT_METHOD_OPTIONS = [
  { id: 'CASH', name: 'Efectivo', icon: MoneyIcon, color: 'success.main' },
  { id: 'CARD', name: 'Tarjeta', icon: CardIcon, color: 'primary.main' },
  { id: 'QR', name: 'QR', icon: QrIcon, color: 'secondary.main' },
];

const getProductImageData = (product: ProductImageData): ProductImageData => ({
  thumbnail: product.thumbnail ?? null,
  thumbnailUrl: product.thumbnailUrl ?? null,
  imagePath: product.imagePath ?? null,
});

interface ExchangeStep2PaymentProps {
  order: SalesOrder;
  exchangeSummary: ExchangeSummary;
  exchangeData: ExchangeFormData;
  paymentFlowPhase: ExchangeStep2FlowPhase;
  setPaymentFlowPhase: (phase: ExchangeStep2FlowPhase) => void;
  paymentsLocked: boolean;
  setPaymentsLocked: (locked: boolean) => void;
  onClearPayments: () => void;
  onAddPayment: (
    method: string,
    amount: number,
    cashReceived?: number,
    notes?: string,
    bankReference?: string,
    qrTransactionId?: string,
  ) => void;
  onRemovePayment: (paymentId: string) => void;
  onUpdatePayment: (
    paymentId: string,
    amount: number,
    cashReceived?: number,
    notes?: string,
    bankReference?: string,
    qrTransactionId?: string,
  ) => void;
  updateNewProductMarkup: (
    productId: number,
    markupType: 'PERCENTAGE' | 'AMOUNT' | 'NONE',
    markupValue: number,
  ) => void;
  updateNewProductDiscount: (
    productId: number,
    discountType: 'PERCENTAGE' | 'AMOUNT' | 'NONE',
    discountValue: number,
  ) => void;
  resetAllLinePricing: () => void;
  commitCommercialPricingConfig: (draft: {
    markupEnabled: boolean;
    allowIndividualMarkups: boolean;
    markupType: 'PERCENTAGE' | 'AMOUNT';
    markupValue: number;
    discountEnabled: boolean;
    allowIndividualDiscounts: boolean;
    discountType: 'PERCENTAGE' | 'AMOUNT';
    discountValue: number;
  }) => void;
  updateMarkupConfig: (config: Partial<MarkupConfig>) => void;
  updateDiscountConfig: (config: Partial<DiscountConfig>) => void;
  applyUniformMarkupToNewProducts: (
    markupType: 'PERCENTAGE' | 'AMOUNT',
    markupValue: number,
  ) => void;
  applyUniformDiscountToNewProducts: (
    discountType: 'PERCENTAGE' | 'AMOUNT',
    discountValue: number,
  ) => void;
  onPartialPaymentToggle: (enabled: boolean) => void;
  onMultiplePaymentsToggle: (enabled: boolean) => void;
  getProductFinalPrice: (product: SelectedProduct) => number;
  formatCurrency: (amount: number) => string;
}

const ExchangeStep2Payment: React.FC<ExchangeStep2PaymentProps> = ({
  order,
  exchangeSummary,
  exchangeData,
  paymentFlowPhase,
  setPaymentFlowPhase,
  paymentsLocked,
  setPaymentsLocked,
  onClearPayments,
  onAddPayment,
  onRemovePayment,
  onUpdatePayment,
  updateNewProductMarkup,
  updateNewProductDiscount,
  resetAllLinePricing,
  commitCommercialPricingConfig,
  updateMarkupConfig,
  updateDiscountConfig,
  applyUniformMarkupToNewProducts,
  applyUniformDiscountToNewProducts,
  onPartialPaymentToggle,
  onMultiplePaymentsToggle,
  getProductFinalPrice,
  formatCurrency,
}) => {
  const {
    flowPhase,
    configEditable,
    productsEditable,
    productsSectionActive,
    hasCommercialConfig,
    paymentConfigVisible,
    paymentConfigEditable,
    paymentMethodsEditable,
    paymentsSectionVisible,
    paymentsEditable,
    commitPricingConfig,
    confirmProductsSection,
    confirmPaymentConfigSection,
    unlockConfigForEdit,
    unlockProductsForEdit,
    unlockPaymentConfigForEdit,
    unlockPaymentMethodsForEdit,
    canConfirmConfigSection,
    canConfirmProductsSection,
    hasInvalidLinePricing,
    violatesCredit,
    summaryIncoherent,
    pricingDraft,
    maxUniformDiscountAmount,
    catalogAdjustmentsAllowed,
    handleMarkupToggle,
    handleIndividualMarkupToggle,
    handleMarkupTypeChange,
    handleMarkupValueChange,
    handleDiscountToggle,
    handleIndividualDiscountToggle,
    handleDiscountTypeChange,
    handleDiscountValueChange,
  } = useExchangeStep2Flow({
    exchangeData,
    exchangeSummary,
    paymentFlowPhase,
    setPaymentFlowPhase,
    paymentsLocked,
    setPaymentsLocked,
    onClearPayments,
    resetAllLinePricing,
    commitCommercialPricingConfig,
    updateMarkupConfig,
    updateDiscountConfig,
    applyUniformMarkupToNewProducts,
    applyUniformDiscountToNewProducts,
  });

  const { otherPaymentMethods, getPaymentMethodName } = useOtherPaymentMethods();
  const [showOtherMethodsDialog, setShowOtherMethodsDialog] = useState(false);
  const [editingPaymentId, setEditingPaymentId] = useState<string | null>(null);
  const [showPaymentAmountDialog, setShowPaymentAmountDialog] = useState(false);
  const [pendingPaymentMethod, setPendingPaymentMethod] = useState<string | null>(null);

  const noAdditionalPayment = !exchangeSummary.requiresPayment;
  const showProductsSection = hasCommercialConfig || flowPhase === 2;

  const itemsToExchange = useMemo(
    () => order.items.filter((item) => exchangeData.itemsToExchange.includes(item.id)),
    [order.items, exchangeData.itemsToExchange],
  );

  useEffect(() => {
    if (violatesCredit && paymentFlowPhase >= 3) {
      setPaymentFlowPhase(hasCommercialConfig ? 2 : 1);
      onClearPayments();
      setPaymentsLocked(false);
    }
  }, [
    violatesCredit,
    paymentFlowPhase,
    hasCommercialConfig,
    setPaymentFlowPhase,
    onClearPayments,
    setPaymentsLocked,
  ]);

  const closePaymentUi = useCallback(() => {
    setShowPaymentAmountDialog(false);
    setShowOtherMethodsDialog(false);
    setPendingPaymentMethod(null);
    setEditingPaymentId(null);
  }, []);

  const getTotalPayments = useCallback(() => {
    const sum = exchangeData.payments?.reduce((total, p) => total + Number(p.amount || 0), 0) || 0;
    return roundMoney(sum);
  }, [exchangeData.payments]);

  const getRemainingBalance = useCallback(() => {
    if (!exchangeSummary.requiresPayment) return 0;
    return roundMoney(Math.max(0, exchangeSummary.difference - getTotalPayments()));
  }, [exchangeSummary.requiresPayment, exchangeSummary.difference, getTotalPayments]);

  const handlePaymentMethodClick = useCallback(
    (method: string) => {
      if (!paymentsEditable) return;
      const isSinglePaymentMode = !exchangeData.paymentConfig?.allowMultiplePayments;
      const isPartialOff = !exchangeData.paymentConfig?.allowPartialPayment;
      if (isSinglePaymentMode && (exchangeData.payments?.length ?? 0) >= 1) {
        return;
      }

      if (isSinglePaymentMode && isPartialOff) {
        if (method !== PAYMENT_METHODS.CASH) {
          onAddPayment(method, roundMoney(exchangeSummary.difference));
          return;
        }
      }

      setPendingPaymentMethod(method);
      setShowPaymentAmountDialog(true);
    },
    [
      paymentsEditable,
      exchangeData.paymentConfig,
      exchangeData.payments,
      exchangeSummary.difference,
      onAddPayment,
    ],
  );

  const handleOtherMethodSelect = useCallback(
    (method: PaymentMethod) => {
      if (!paymentsEditable) return;
      const isSinglePaymentMode = !exchangeData.paymentConfig?.allowMultiplePayments;
      const isPartialOff = !exchangeData.paymentConfig?.allowPartialPayment;
      if (isSinglePaymentMode && (exchangeData.payments?.length ?? 0) >= 1) {
        return;
      }

      if (isSinglePaymentMode && isPartialOff) {
        onAddPayment(method.code, roundMoney(exchangeSummary.difference));
        setShowOtherMethodsDialog(false);
        return;
      }

      setPendingPaymentMethod(method.code);
      setShowOtherMethodsDialog(false);
      setShowPaymentAmountDialog(true);
    },
    [
      paymentsEditable,
      exchangeData.paymentConfig,
      exchangeData.payments,
      exchangeSummary.difference,
      onAddPayment,
    ],
  );

  const handleOpenOtherMethods = useCallback(() => {
    if (!paymentsEditable) return;
    if (otherPaymentMethods.length > 0) {
      setShowOtherMethodsDialog(true);
    }
  }, [paymentsEditable, otherPaymentMethods.length]);

  const handleAddPaymentWithAmount = useCallback(
    (
      method: string,
      amount: number,
      cashReceived?: number,
      notes?: string,
      bankReference?: string,
      qrTransactionId?: string,
    ) => {
      onAddPayment(method, amount, cashReceived, notes, bankReference, qrTransactionId);
      closePaymentUi();
    },
    [onAddPayment, closePaymentUi],
  );

  const handleUpdatePaymentAmount = useCallback(
    (
      paymentId: string,
      amount: number,
      cashReceived?: number,
      notes?: string,
      bankReference?: string,
      qrTransactionId?: string,
    ) => {
      onUpdatePayment(paymentId, amount, cashReceived, notes, bankReference, qrTransactionId);
      closePaymentUi();
    },
    [onUpdatePayment, closePaymentUi],
  );

  const handleEditPayment = useCallback(
    (paymentId: string) => {
      if (!paymentsEditable) return;
      setEditingPaymentId(paymentId);
      setShowPaymentAmountDialog(true);
    },
    [paymentsEditable],
  );

  const handleRemovePaymentClick = useCallback(
    (paymentId: string) => {
      if (!paymentsEditable) return;
      onRemovePayment(paymentId);
    },
    [paymentsEditable, onRemovePayment],
  );

  const handleClosePaymentAmountDialog = useCallback(() => {
    closePaymentUi();
  }, [closePaymentUi]);

  const formatAdjustmentDisplay = useCallback(
    (adjustmentType: string, adjustmentValue: number, basePrice: number) =>
      formatPriceAdjustmentDisplay(adjustmentType, adjustmentValue, basePrice),
    [],
  );

  const handleProductMarkupChange = useCallback(
    (productId: number, type: 'PERCENTAGE' | 'AMOUNT', value: number) => {
      const product = exchangeData.newProducts.find(
        (item) => item.warehouseInventoryProduct.id === productId,
      );
      if (product && isCourtesyCatalogLine(product.originalPrice)) {
        return;
      }
      updateNewProductMarkup(productId, type, roundMoney(Math.max(0, value)));
    },
    [exchangeData.newProducts, updateNewProductMarkup],
  );

  const handleProductDiscountChange = useCallback(
    (productId: number, type: 'PERCENTAGE' | 'AMOUNT' | 'NONE', value: number) => {
      const product = exchangeData.newProducts.find(
        (item) => item.warehouseInventoryProduct.id === productId,
      );
      if (product && isCourtesyCatalogLine(product.originalPrice)) {
        return;
      }
      updateNewProductDiscount(productId, type, value);
    },
    [exchangeData.newProducts, updateNewProductDiscount],
  );

  const canProceed = useMemo(() => {
    if (flowPhase !== 4) return false;
    if (violatesCredit) return false;
    if (!exchangeSummary.requiresPayment) return true;
    const allowPartial = exchangeData.paymentConfig?.allowPartialPayment ?? false;
    if ((exchangeData.payments?.length ?? 0) === 0) return false;
    if (exchangeData.payments?.some((p) => p.amount <= 0)) return false;
    if (allowPartial) return getTotalPayments() > 0;
    return getRemainingBalance() <= 0;
  }, [
    flowPhase,
    violatesCredit,
    exchangeSummary.requiresPayment,
    exchangeData.paymentConfig,
    exchangeData.payments,
    getTotalPayments,
    getRemainingBalance,
  ]);

  return (
    <MainContainer>
      <LeftColumn>
        {/* Phase 1: Configuración comercial */}
        <Box sx={{ mb: 3 }}>
          <Card sx={{ bgcolor: 'background.paper' }}>
            <CardContent>
              <Box
                sx={{
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TuneIcon color="primary" />
                  Configuración comercial
                </Typography>
                {flowPhase > 1 && (
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={unlockConfigForEdit}
                    sx={{ cursor: 'pointer' }}
                  >
                    Modificar configuración
                  </Link>
                )}
              </Box>

              <Box sx={sectionPhaseSx(flowPhase === 1)}>
                <LinePricingToolbar
                  editable={configEditable}
                  adjustmentsAllowed={catalogAdjustmentsAllowed}
                  markupEnabled={pricingDraft.markupEnabled}
                  allowIndividualMarkups={pricingDraft.allowIndividualMarkups}
                  discountEnabled={pricingDraft.discountEnabled}
                  allowIndividualDiscounts={pricingDraft.allowIndividualDiscounts}
                  markupType={pricingDraft.markupType}
                  markupValue={pricingDraft.markupValue}
                  discountType={pricingDraft.discountType}
                  discountValue={pricingDraft.discountValue}
                  maxUniformDiscountAmount={maxUniformDiscountAmount}
                  onMarkupToggle={handleMarkupToggle}
                  onIndividualMarkupToggle={handleIndividualMarkupToggle}
                  onMarkupTypeChange={handleMarkupTypeChange}
                  onMarkupValueChange={handleMarkupValueChange}
                  onDiscountToggle={handleDiscountToggle}
                  onIndividualDiscountToggle={handleIndividualDiscountToggle}
                  onDiscountTypeChange={handleDiscountTypeChange}
                  onDiscountValueChange={handleDiscountValueChange}
                />

                {exchangeSummary.paidForExchangedItems > 0 && flowPhase === 1 && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 1, mb: 0.5 }}
                  >
                    Crédito del intercambio: {formatCurrency(exchangeSummary.paidForExchangedItems)}
                  </Typography>
                )}

                {flowPhase === 1 && (
                  <SubStepGhostContinueButton
                    fullWidth
                    onClick={commitPricingConfig}
                    disabled={!canConfirmConfigSection}
                    startIcon={<AddIcon sx={{ fontSize: 20 }} />}
                    sx={{ mt: 1.5 }}
                  >
                    {hasCommercialConfig ? 'Aplicar a piezas' : 'Continuar a configuración de pago'}
                  </SubStepGhostContinueButton>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Phase 2: Piezas (solo si hay config comercial) */}
        {showProductsSection && (
          <Box sx={{ mb: 3 }}>
            <Card sx={{ bgcolor: 'background.paper' }}>
              <CardContent>
                <Box
                  sx={{
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InventoryIcon color="primary" />
                    Piezas ({exchangeData.newProducts.length})
                  </Typography>
                  {flowPhase > 2 && productsSectionActive && (
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={unlockProductsForEdit}
                      sx={{ cursor: 'pointer' }}
                    >
                      Modificar piezas
                    </Link>
                  )}
                </Box>

                <Box sx={sectionPhaseSx(productsSectionActive || flowPhase === 2)}>
                  {flowPhase < 2 && (
                    <SalesStepHint tone="info">
                      Confirma la configuración comercial para ver el detalle por pieza
                    </SalesStepHint>
                  )}

                  {flowPhase >= 2 && productsSectionActive && itemsToExchange.length > 0 && (
                    <Box sx={{ mb: 2.5 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, mb: 1.5, color: 'text.secondary' }}
                      >
                        Piezas a intercambiar ({itemsToExchange.length})
                      </Typography>
                      {itemsToExchange.map((item) => (
                        <Box
                          key={item.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            py: 0.75,
                            borderBottom: 1,
                            borderColor: 'divider',
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                              {item.warehouseInventoryProduct.product.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Código: {item.warehouseInventoryProduct.productCodeUnique || 'N/A'}
                            </Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: 'text.secondary', flexShrink: 0 }}
                          >
                            {formatCurrency(item.finalPrice)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {flowPhase >= 2 &&
                    productsSectionActive &&
                    exchangeData.newProducts.map((product) => (
                      <SalesLinePricingCard
                        key={product.warehouseInventoryProduct.id}
                        product={product}
                        editable={productsEditable}
                        showMarkupControls={pricingDraft.markupEnabled}
                        showIndividualMarkupControls={pricingDraft.allowIndividualMarkups}
                        showDiscountControls={exchangeData.discountConfig?.enabled ?? false}
                        showIndividualDiscountControls={
                          exchangeData.discountConfig?.allowIndividualDiscounts ?? false
                        }
                        onMarkupChange={handleProductMarkupChange}
                        onDiscountChange={handleProductDiscountChange}
                        formatAdjustmentDisplay={formatAdjustmentDisplay}
                      />
                    ))}

                  {flowPhase === 2 && hasInvalidLinePricing && (
                    <Alert severity="warning" sx={{ mb: 2, mt: 1 }}>
                      {summaryIncoherent
                        ? 'El resumen del intercambio no cuadra. Ajusta los precios.'
                        : getExchangeCreditViolationMessage(
                            exchangeSummary.paidForExchangedItems,
                            formatCurrency,
                          )}
                    </Alert>
                  )}

                  {flowPhase === 2 && (
                    <SubStepGhostContinueButton
                      fullWidth
                      onClick={confirmProductsSection}
                      disabled={!canConfirmProductsSection}
                      startIcon={<AddIcon sx={{ fontSize: 20 }} />}
                      sx={{ mt: 1.5 }}
                    >
                      Continuar a configuración de pago
                    </SubStepGhostContinueButton>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Phase 3: Configuración de pago */}
        <Box sx={{ mb: 3 }}>
          <Card sx={{ bgcolor: 'background.paper' }}>
            <CardContent>
              <Box
                sx={{
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.primary' }}
                >
                  <PaymentIcon color="primary" />
                  Configuración de pago
                </Typography>
                {flowPhase > 3 && (
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={unlockPaymentConfigForEdit}
                    sx={{ cursor: 'pointer' }}
                  >
                    Modificar configuración
                  </Link>
                )}
              </Box>

              <Box sx={sectionPhaseSx(paymentConfigVisible)}>
                {!paymentConfigVisible && (
                  <SalesStepHint tone="info">
                    {hasCommercialConfig
                      ? 'Confirma las piezas para configurar el pago'
                      : 'Confirma la configuración comercial para configurar el pago'}
                  </SalesStepHint>
                )}

                {paymentConfigVisible && noAdditionalPayment && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 2 }}
                  >
                    No hay pago adicional en este intercambio.
                  </Typography>
                )}

                {paymentConfigVisible && !noAdditionalPayment && (
                  <>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} md={6}>
                        <PaymentToggleBox
                          isActive={exchangeData.paymentConfig?.allowPartialPayment ?? false}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              mb: 1,
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <MoneyIcon
                                color={
                                  exchangeData.paymentConfig?.allowPartialPayment
                                    ? 'success'
                                    : 'action'
                                }
                                sx={{ fontSize: 16 }}
                              />
                              <Typography
                                variant="caption"
                                fontWeight={600}
                                color={
                                  exchangeData.paymentConfig?.allowPartialPayment
                                    ? 'success.main'
                                    : 'text.primary'
                                }
                                sx={{ fontSize: '0.75rem' }}
                              >
                                Pago Parcial
                              </Typography>
                            </Box>
                            <FlagSwitch
                              checked={exchangeData.paymentConfig?.allowPartialPayment ?? false}
                              onChange={(_, checked) => onPartialPaymentToggle(checked)}
                              disabled={!paymentConfigEditable}
                            />
                          </Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}
                          >
                            {exchangeData.paymentConfig?.allowPartialPayment
                              ? 'Pago parcial permitido'
                              : 'Pago total obligatorio'}
                          </Typography>
                        </PaymentToggleBox>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <PaymentToggleBox
                          isActive={exchangeData.paymentConfig?.allowMultiplePayments ?? false}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              mb: 1,
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <PaymentIcon
                                color={
                                  exchangeData.paymentConfig?.allowMultiplePayments
                                    ? 'success'
                                    : 'action'
                                }
                                sx={{ fontSize: 16 }}
                              />
                              <Typography
                                variant="caption"
                                fontWeight={600}
                                color={
                                  exchangeData.paymentConfig?.allowMultiplePayments
                                    ? 'success.main'
                                    : 'text.primary'
                                }
                                sx={{ fontSize: '0.75rem' }}
                              >
                                Múltiples pagos
                              </Typography>
                            </Box>
                            <FlagSwitch
                              checked={exchangeData.paymentConfig?.allowMultiplePayments ?? false}
                              onChange={(_, checked) => onMultiplePaymentsToggle(checked)}
                              disabled={!paymentConfigEditable}
                            />
                          </Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}
                          >
                            {exchangeData.paymentConfig?.allowMultiplePayments
                              ? 'Permite agregar varios pagos.'
                              : 'Solo se permite un pago.'}
                          </Typography>
                        </PaymentToggleBox>
                      </Grid>
                    </Grid>
                  </>
                )}

                {flowPhase === 3 && (
                  <SubStepGhostContinueButton
                    fullWidth
                    onClick={confirmPaymentConfigSection}
                    startIcon={<AddIcon sx={{ fontSize: 20 }} />}
                    sx={{ mt: 1.5 }}
                  >
                    Continuar a métodos de pago
                  </SubStepGhostContinueButton>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Phase 4: Métodos de pago */}
        <Box sx={{ mb: 3 }}>
          <Card sx={{ mb: 3, bgcolor: 'background.paper' }}>
            <CardContent>
              <Box
                sx={{
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.primary' }}
                >
                  <PaymentIcon color="primary" />
                  Métodos de Pago
                </Typography>
                {paymentsLocked && (
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={unlockPaymentMethodsForEdit}
                    sx={{ cursor: 'pointer' }}
                  >
                    Modificar métodos
                  </Link>
                )}
              </Box>

              <Box sx={sectionPhaseSx(paymentsSectionVisible)}>
                {!paymentsSectionVisible && (
                  <SalesStepHint tone="info">
                    Confirma la configuración de pago para registrar métodos
                  </SalesStepHint>
                )}

                {noAdditionalPayment && paymentsSectionVisible && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 2 }}
                  >
                    No hay pago adicional en este intercambio.
                  </Typography>
                )}

                <Box sx={sectionPhaseSx(paymentMethodsEditable && !noAdditionalPayment)}>
                  {!noAdditionalPayment && paymentsSectionVisible && (
                    <>
                      <Grid container spacing={1.5} sx={{ mb: 3 }}>
                        {PAYMENT_METHOD_OPTIONS.map((method) => {
                          const IconComponent = method.icon;
                          return (
                            <Grid item xs={3} key={method.id}>
                              <PaymentMethodButton
                                fullWidth
                                variant="outlined"
                                onClick={() => handlePaymentMethodClick(method.id)}
                                disabled={
                                  !paymentMethodsEditable ||
                                  (!exchangeData.paymentConfig?.allowMultiplePayments &&
                                    (exchangeData.payments?.length ?? 0) >= 1)
                                }
                                isSelected={false}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 0.25,
                                  }}
                                >
                                  <PaymentMethodIconBox isSelected={false}>
                                    <IconComponent sx={{ fontSize: 16, color: 'text.secondary' }} />
                                  </PaymentMethodIconBox>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: 500,
                                      fontSize: '0.75rem',
                                      textAlign: 'center',
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    {method.name}
                                  </Typography>
                                </Box>
                              </PaymentMethodButton>
                            </Grid>
                          );
                        })}
                        <Grid item xs={3}>
                          <PaymentMethodButton
                            fullWidth
                            variant="outlined"
                            onClick={handleOpenOtherMethods}
                            disabled={
                              !paymentMethodsEditable ||
                              otherPaymentMethods.length === 0 ||
                              (!exchangeData.paymentConfig?.allowMultiplePayments &&
                                (exchangeData.payments?.length ?? 0) >= 1)
                            }
                            isSelected={false}
                            sx={{
                              opacity: otherPaymentMethods.length === 0 ? 0.5 : 1,
                              cursor: otherPaymentMethods.length === 0 ? 'not-allowed' : 'pointer',
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 0.25,
                              }}
                            >
                              <PaymentMethodIconBox isSelected={false}>
                                <OtherIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              </PaymentMethodIconBox>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 500,
                                  fontSize: '0.75rem',
                                  textAlign: 'center',
                                  lineHeight: 1.2,
                                }}
                              >
                                Otro
                              </Typography>
                            </Box>
                          </PaymentMethodButton>
                        </Grid>
                      </Grid>

                      {(exchangeData.payments?.length ?? 0) === 0 ? (
                        <SalesStepHint tone="success">
                          No hay pagos agregados. Seleccione un método de pago para agregar un pago.
                        </SalesStepHint>
                      ) : (
                        <Box sx={{ mb: 3 }}>
                          {exchangeData.payments?.map((payment) => {
                            const isCash = payment.method === 'CASH';
                            const IconComponent =
                              PAYMENT_METHOD_OPTIONS.find((m) => m.id === payment.method)?.icon ??
                              PaymentIcon;
                            return (
                              <Card
                                key={payment.id}
                                sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}
                              >
                                <CardContent>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'flex-start',
                                    }}
                                  >
                                    <Box sx={{ flex: 1 }}>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 1,
                                          mb: 1,
                                        }}
                                      >
                                        <IconComponent fontSize="small" color="primary" />
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                          {getPaymentMethodName(payment.method)}
                                        </Typography>
                                      </Box>
                                      <Typography
                                        variant="h6"
                                        sx={{ color: 'primary.main', mb: 1 }}
                                      >
                                        {formatCurrency(payment.amount)}
                                      </Typography>
                                      {isCash &&
                                        payment.cashReceived != null &&
                                        payment.cashReceived > payment.amount && (
                                          <Typography variant="body2" color="text.secondary">
                                            Recibido: {formatCurrency(payment.cashReceived)}
                                            {' - '}
                                            Cambio:{' '}
                                            {formatCurrency(payment.cashReceived - payment.amount)}
                                          </Typography>
                                        )}
                                      {payment.paymentNotes != null &&
                                        payment.paymentNotes !== '' && (
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ display: 'block', mt: 0.5 }}
                                          >
                                            {payment.paymentNotes}
                                          </Typography>
                                        )}
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                      <Button
                                        size="small"
                                        startIcon={<EditIcon />}
                                        onClick={() => handleEditPayment(payment.id)}
                                        disabled={!paymentsEditable}
                                      >
                                        Editar
                                      </Button>
                                      <Button
                                        size="small"
                                        color="error"
                                        startIcon={<DeleteIcon />}
                                        onClick={() => handleRemovePaymentClick(payment.id)}
                                        disabled={!paymentsEditable}
                                      >
                                        Eliminar
                                      </Button>
                                    </Box>
                                  </Box>
                                </CardContent>
                              </Card>
                            );
                          })}
                        </Box>
                      )}

                      {(exchangeData.payments?.length ?? 0) > 0 && (
                        <Box
                          sx={(theme) => ({
                            p: 2,
                            backgroundColor:
                              theme.palette.mode === 'dark'
                                ? alpha(theme.palette.common.white, 0.06)
                                : theme.palette.grey[50],
                            border:
                              theme.palette.mode === 'dark'
                                ? `1px solid ${theme.palette.divider}`
                                : 'none',
                            borderRadius: 1,
                            mb: 2,
                          })}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Monto a pagar:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {formatCurrency(exchangeSummary.difference)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Total pagado:
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: 'primary.main' }}
                            >
                              {formatCurrency(getTotalPayments())}
                            </Typography>
                          </Box>
                          {getRemainingBalance() > 0 ? (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">
                                Saldo pendiente:
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, color: 'warning.main' }}
                              >
                                {formatCurrency(getRemainingBalance())}
                              </Typography>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">
                                Cambio:
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, color: 'success.main' }}
                              >
                                {formatCurrency(exchangeSummary.change)}
                              </Typography>
                            </Box>
                          )}
                          {exchangeSummary.paymentStatus === 'complete' && (
                            <SalesStepHint tone="success" sx={{ mb: 0, mt: 2 }}>
                              Pago completo
                            </SalesStepHint>
                          )}
                          {exchangeSummary.paymentStatus === 'partial' && (
                            <SalesStepHint tone="warning" sx={{ mb: 0, mt: 2 }}>
                              Pago parcial - Saldo pendiente:{' '}
                              {formatCurrency(getRemainingBalance())}
                            </SalesStepHint>
                          )}
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </Box>

              <Dialog
                open={showOtherMethodsDialog}
                onClose={() => setShowOtherMethodsDialog(false)}
                maxWidth="sm"
                fullWidth
              >
                <DialogTitle>Seleccionar Método de Pago</DialogTitle>
                <DialogContent>
                  {otherPaymentMethods.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No hay otros métodos de pago disponibles.
                    </Typography>
                  ) : (
                    <List>
                      {otherPaymentMethods.map((method: PaymentMethod) => (
                        <ListItem key={method.id} disablePadding>
                          <ListItemButton onClick={() => handleOtherMethodSelect(method)}>
                            <ListItemIcon>
                              <PaymentIcon />
                            </ListItemIcon>
                            <ListItemText
                              primary={method.name}
                              secondary={method.description ?? method.code}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </DialogContent>
                <DialogActions>
                  <ModalCancelButton
                    variant="tonal"
                    onClick={() => setShowOtherMethodsDialog(false)}
                  >
                    Cancelar
                  </ModalCancelButton>
                </DialogActions>
              </Dialog>

              <ExchangePaymentAmountDialog
                open={showPaymentAmountDialog}
                onClose={handleClosePaymentAmountDialog}
                method={
                  pendingPaymentMethod ??
                  (editingPaymentId != null
                    ? (exchangeData.payments?.find((p) => p.id === editingPaymentId)?.method ??
                      null)
                    : null)
                }
                editingPayment={
                  editingPaymentId != null
                    ? (exchangeData.payments?.find((p) => p.id === editingPaymentId) ?? null)
                    : null
                }
                remainingBalance={getRemainingBalance()}
                onAdd={handleAddPaymentWithAmount}
                onUpdate={handleUpdatePaymentAmount}
                formatCurrency={formatCurrency}
              />
            </CardContent>
          </Card>
        </Box>
      </LeftColumn>

      <RightColumn>
        <Card sx={{ mb: 3, bgcolor: 'background.paper' }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <ExchangeIcon color="primary" />
              Resumen de Intercambio
            </Typography>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Sucursal
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                {order.location.name}
              </Typography>
            </Box>

            {itemsToExchange.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                  Piezas a intercambiar ({itemsToExchange.length})
                </Typography>
                <Box>
                  {itemsToExchange.map((item) => (
                    <ProductCardBox key={item.id}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <ProductImagePreview
                          product={getProductImageData(
                            item.warehouseInventoryProduct.product as ProductImageData,
                          )}
                          alt={item.warehouseInventoryProduct.product.name}
                          size={60}
                          borderRadius={10}
                        />
                        <Box
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 1.5,
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
                              {item.warehouseInventoryProduct.product.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', mt: 0.5 }}
                            >
                              Código: {item.warehouseInventoryProduct.productCodeUnique || 'N/A'}
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: 'primary.main' }}
                            >
                              {formatCurrency(item.finalPrice)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </ProductCardBox>
                  ))}
                </Box>
              </Box>
            )}

            {exchangeData.newProducts.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                  Piezas nuevas ({exchangeData.newProducts.length})
                </Typography>
                <Box>
                  {exchangeData.newProducts.map((product) => (
                    <ProductCardBox key={product.warehouseInventoryProduct.id}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <ProductImagePreview
                          product={getProductImageData(
                            product.warehouseInventoryProduct.product as ProductImageData,
                          )}
                          alt={product.warehouseInventoryProduct.product.name}
                          size={60}
                          borderRadius={10}
                        />
                        <Box
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 1.5,
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
                              {product.warehouseInventoryProduct.product.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', mt: 0.5 }}
                            >
                              Código: {product.warehouseInventoryProduct.productCodeUnique || 'N/A'}
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: 'success.main' }}
                            >
                              {formatCurrency(getProductFinalPrice(product))}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </ProductCardBox>
                  ))}
                </Box>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Subtotal catálogo:
                </Typography>
                <Typography variant="body2">
                  {formatCurrency(exchangeSummary.newItemsSubtotal)}
                </Typography>
              </Box>
              {exchangeSummary.markupAmount > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="success.main">
                    Incrementos:
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    +{formatCurrency(exchangeSummary.markupAmount)}
                  </Typography>
                </Box>
              )}
              {exchangeSummary.discountAmount > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="error">
                    Descuentos:
                  </Typography>
                  <Typography variant="body2" color="error">
                    -{formatCurrency(exchangeSummary.discountAmount)}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Total piezas nuevas:
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  color={violatesCredit ? 'error.main' : 'text.primary'}
                >
                  {formatCurrency(exchangeSummary.newItemsTotal)}
                </Typography>
              </Box>
              {exchangeSummary.paidForExchangedItems > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Crédito del intercambio:
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color={violatesCredit ? 'error.main' : 'text.primary'}
                  >
                    {formatCurrency(exchangeSummary.paidForExchangedItems)}
                  </Typography>
                </Box>
              )}
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {violatesCredit ? 'Diferencia (inválida):' : 'Diferencia a pagar:'}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {formatCurrency(exchangeSummary.difference)}
                </Typography>
              </Box>
            </Box>

            {(exchangeData.payments?.length ?? 0) > 0 && (
              <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                  Información de Pago
                </Typography>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Total pagado:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatCurrency(getTotalPayments())}
                    </Typography>
                  </Box>
                  {getRemainingBalance() > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="warning.main">
                        Saldo pendiente:
                      </Typography>
                      <Typography variant="body2" color="warning.main" sx={{ fontWeight: 600 }}>
                        {formatCurrency(getRemainingBalance())}
                      </Typography>
                    </Box>
                  )}
                  {exchangeSummary.change > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="success.main">
                        Cambio:
                      </Typography>
                      <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                        {formatCurrency(exchangeSummary.change)}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Pago parcial:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: exchangeData.paymentConfig?.allowPartialPayment
                            ? 'success.main'
                            : 'text.secondary',
                        }}
                      >
                        {exchangeData.paymentConfig?.allowPartialPayment
                          ? 'Habilitado'
                          : 'Deshabilitado'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>

        {paymentsSectionVisible && !canProceed && exchangeSummary.requiresPayment && (
          <Alert
            severity="error"
            sx={(theme) => ({
              mt: 2,
              backgroundColor: alpha(
                theme.palette.error.main,
                theme.palette.mode === 'dark' ? 0.14 : 0.08,
              ),
              color: theme.palette.error.light,
              border: `1px solid ${alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.4 : 0.35)}`,
              '& .MuiAlert-icon': {
                color: theme.palette.error.light,
              },
            })}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'error.light' }}>
              Pendientes:
            </Typography>
            <ValidationList>
              {(exchangeData.payments?.length ?? 0) === 0 && (
                <Typography component="li" variant="caption" sx={{ color: 'error.light' }}>
                  • Agregar al menos un método de pago
                </Typography>
              )}
              {(exchangeData.payments?.length ?? 0) > 0 &&
                exchangeData.payments?.some((p) => p.amount <= 0) && (
                  <Typography component="li" variant="caption" sx={{ color: 'error.light' }}>
                    • Todos los pagos deben tener un monto mayor a cero
                  </Typography>
                )}
              {(exchangeData.payments?.length ?? 0) > 0 &&
                !exchangeData.paymentConfig?.allowPartialPayment &&
                getRemainingBalance() > 0 && (
                  <Typography component="li" variant="caption" sx={{ color: 'error.light' }}>
                    • El pago debe cubrir el total completo (saldo pendiente:{' '}
                    {formatCurrency(getRemainingBalance())})
                  </Typography>
                )}
              {violatesCredit && (
                <Typography component="li" variant="caption" sx={{ color: 'error.light' }}>
                  •{' '}
                  {getExchangeCreditViolationMessage(
                    exchangeSummary.paidForExchangedItems,
                    formatCurrency,
                  )}
                </Typography>
              )}
            </ValidationList>
          </Alert>
        )}
      </RightColumn>
    </MainContainer>
  );
};

export default ExchangeStep2Payment;
